import React, { useState } from "react";
import {
  MyMessage,
  InputField,
  Select,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { codeOk } from "../../../lib/index";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { getOneNeighborhood } from "../../../lib/sysNavigation/SysNavigation";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import GenerateDescription from "../utils/GenerateDescription";
import { DescriptionSection } from "../utils/AddSections";
import { FiImage, FiShield, FiSettings, FiMapPin } from "react-icons/fi";

const Edit = () => {
  const { updateOneContent } = useDoSeeDataContext();
  const neighborhood: any = getOneNeighborhood();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState<any>("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const neighborhood_id = neighborhood?.neighborhood_id;
  const [title, setTitle] = useState<any>(neighborhood?.name);
  const [thumbnail, setThumbnail] = useState<any>(neighborhood?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState<any>(
    neighborhood?.image_cdn_id
  );
  const [safety, setSafety] = useState<any>(neighborhood?.safety_indicator);
  const [neighborhoodStatus, setNeighborhoodStatus] = useState<any>(
    neighborhood?.status
  );
  const [description, setDescription] = useState<any>(neighborhood?.description);
  const [expo, setExpo] = useState<any>(neighborhood?.expo);
  const neighborhoodRoutes = "/data/neighborhood";
  const [showDescriptionModal, setShowDescriptionModal] = useState<any>(null);

  const responses = (isError: any, message: any) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(message);
  };

  const updateOneNeighborhoodHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id ? country_id : neighborhood?.country_id,
      first_level_id: first_level_id ? first_level_id : 0,
      second_level_id: second_level_id ? second_level_id : 0,
      third_level_id: third_level_id ? third_level_id : 0,
      fourth_level_id: fourth_level_id ? fourth_level_id : 0,
      name: title,
      slug: title ? title.toLowerCase().replace(/\s/g, "_") : null,
      safety_indicator: safety,
      description: description,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "",
      status: neighborhoodStatus,
      expo: expo,
    };

    const { message, status } = await updateOneContent({
      data_id: neighborhood_id,
      data: data,
      content: _TYPES_.NEIGHBORHOOD,
      position: {
        country_id,
        first_level_id,
        second_level_id,
        third_level_id,
        fourth_level_id,
      },
    });

    if (status === codeOk) responses(false, message);
    else responses(true, message);
  };

  const clearMessages = () => {
    setError(null as any);
    setSuccess(null as any);
  };

  return (
    <AddPageShell
      backRoute={neighborhoodRoutes}
      eyebrow="DoSee Data • Neighborhoods"
      title="Edit Neighborhood"
      subtitle={`Update neighborhood info for ${entry_label || "this destination"}.`}
      icon={<FiMapPin className="h-5 w-5" />}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={updateOneNeighborhoodHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Cover Image"
        hint="A street view or skyline photo works well."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.NEIGHBORHOODS)}
        />
      </AddSection>

      <AddSection
        title="Basics & Safety"
        hint="Name and safety perception."
        icon={<FiShield className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              value: title || "",
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
          <Select
            props={{
              id: "safety_indicator",
              title: "Safety Indicator",
              option: ["safe", "intermediate", "danger"],
              preselect: safety ? [{ name: safety }] : null,
              onChange: (e: any) => {
                setSafety(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Visibility"
        hint="Where this entry shows."
        icon={<FiSettings className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Select
            props={{
              id: "neighborhood_status",
              title: "Status",
              option: ["visible", "invisible"],
              preselect:
                neighborhoodStatus !== undefined &&
                neighborhoodStatus !== null
                  ? [
                      {
                        name:
                          neighborhoodStatus === 1 ? "visible" : "invisible",
                      },
                    ]
                  : null,
              onChange: (e: any) => {
                if (e.target.selectedIndex === 0) setNeighborhoodStatus(1);
                else setNeighborhoodStatus(0);
                clearMessages();
              },
            }}
          />
          <Select
            props={{
              id: "gallery",
              title: "Show in Gallery",
              option: ["no", "yes"],
              preselect:
                expo !== undefined && expo !== null
                  ? [{ name: expo === 0 ? "no" : "yes" }]
                  : null,
              onChange: (e: any) => {
                const choice = e.target.value;
                setExpo(choice === "no" ? 0 : 1);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <DescriptionSection
        description={description}
        setDescription={setDescription}
        onGenerate={() => setShowDescriptionModal(true)}
        clearMessages={clearMessages}
      />
    </AddPageShell>
  );
};

export default Edit;
